// ID45: 배너 노출예약/국가별 링크/노출 — 4 로케일 정본 키.
export type BannerLocale = 'ko' | 'en' | 'ja' | 'zh';
export const BANNER_LOCALES: readonly BannerLocale[] = ['ko', 'en', 'ja', 'zh'] as const;

/** 국가별 직접 링크 — 미지정(null)이면 기본 `href` 폴백(BE 권위). */
export type BannerHrefByLocale = Record<BannerLocale, string | null>;
/** 국가별 노출 토글 — 기본 true. */
export type BannerVisibleByLocale = Record<BannerLocale, boolean>;

export interface HomeBannerAdminView {
  id: number;
  bannerKey: string;
  albumTitle: string;
  artistName: string;
  eyebrow: string;
  tagline: string;
  accentHex: string;
  textColor: string;
  href: string;
  ctaLabel: string;
  imageUrl: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  // C-3: 실제 상품 연동 (BE V49). productId 있으면 href 는 /products/{id} 로 서버 도출.
  productId?: string | null;
  // ID45 (BE V82): 노출 예약 윈도우(Instant ISO-8601, 둘 다 null=상시노출).
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  // ID45: 국가별 링크/노출. 응답은 항상 ko/en/ja/zh 4키(BE 계약).
  hrefByLocale?: BannerHrefByLocale | null;
  visibleByLocale?: BannerVisibleByLocale | null;
  // QA-293: 서브 텍스트(태그라인) 색상 #RRGGBB. null = 쇼핑몰 기본색. BE 반영 전 응답에는 키 자체가 없다.
  taglineColor?: string | null;
}

export interface HomeBannerRequest {
  bannerKey: string;
  albumTitle: string;
  artistName: string;
  eyebrow: string;
  tagline: string;
  accentHex: string;
  textColor: string;
  // C-3: productId 가 있으면 href 는 선택값(서버가 도출). 둘 다 비면 BE 400.
  href: string;
  ctaLabel: string;
  imageUrl: string;
  isActive: boolean;
  sortOrder: number;
  productId: string | null;
  // ID45: 노출예약(Instant ISO-8601 또는 null). end<start → BE 400.
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  // ID45: 국가별 링크/노출. 누락/null 항목은 BE 가 href 폴백 / visible=true 로 처리.
  hrefByLocale?: BannerHrefByLocale;
  visibleByLocale?: BannerVisibleByLocale;
  // QA-293: 서브 텍스트(태그라인) 색상 #RRGGBB 또는 null(쇼핑몰 기본색).
  taglineColor?: string | null;
}

// QA-102: 메인 히어로 헤드라인 캡션 (BE catalog V112 home_hero_caption, locale 단일정본 ko).
//   storefront 가 GET 으로 읽어 'textBefore [highlightWord] textAfter' 헤드라인을 구성한다.
//   highlightColor 는 강조 단어 색(미지정 시 SF accent 기본값). 내부 id/locale 는 노출하지 않는다.
export interface HomeHeroCaptionAdminView {
  textBefore: string;
  highlightWord: string;
  textAfter: string;
  // 강조 단어 색(HEX, 예: '#EE2737'). null/빈값 = SF 기본 accent 색.
  highlightColor: string | null;
  isActive: boolean;
}

export interface HomeHeroCaptionRequest {
  textBefore: string;
  highlightWord: string;
  textAfter: string;
  highlightColor: string | null;
  isActive: boolean;
}

export interface OfflineStoreAdminView {
  id: number;
  storeKey: string;
  name: string;
  address: string;
  hoursLabel: string;
  thumbnailUrl: string;
  href: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  // M-3(ID99-②): 매장 전화번호 (be-event 동결 계약 eb847e0 — nullable, ≤40). 미입력 시 null.
  phoneNumber?: string | null;
}

export interface OfflineStoreRequest {
  storeKey: string;
  name: string;
  address: string;
  hoursLabel: string;
  thumbnailUrl: string;
  href: string;
  isActive: boolean;
  sortOrder: number;
  // M-3(ID99-②): 매장 전화번호 (be-event 동결 계약 eb847e0 — nullable, ≤40). 빈값은 null 전송.
  phoneNumber?: string | null;
}

export interface CmsPage<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// QA-122 — Popup per-locale 번역(be-catalog). locale 별 description(plain)/descriptionHtml(HTML).
// 정본(ko-KR)은 description/descriptionHtml 본필드. descriptionHtml = 트위터 임베드 등 raw HTML.
export interface PopupTranslation {
  description?: string | null;
  descriptionHtml?: string | null;
}

export interface PopupAdminView {
  popupId: string;
  popupKey: string;
  name: string;
  location: string;
  address: string;
  openDate: string;
  closeDate: string;
  hoursLabel: string;
  thumbnailUrl: string;
  accentHex: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  // BUG-022 config 필드 (BE additive — 미존재 시 undefined).
  operatingDays?: string | null;
  operatingHoursStart?: string | null;
  operatingHoursEnd?: string | null;
  operatingUntil?: string | null;
  locationRegion?: string | null;
  locationAddress?: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
  descriptionHtml?: string | null;
  snsUrls?: string | null;
  basePrice?: number | null;
  // J: 슬롯 집계 (BE additive). reservedCount=점유 예약 인원합, capacity=슬롯 정원합. 슬롯 없으면 0.
  reservedCount?: number | null;
  capacity?: number | null;
  // ID36: 일별 총 정원(null=무제한), 잔여석 공개 노출 토글. admin 응답 항상 포함(BE additive).
  dailyCapacity?: number | null;
  showRemainingSeats?: boolean | null;
  // QA-183(V109): 예약 수량 제한(null=무제한). admin 응답 additive.
  maxPerUser?: number | null;
  minPerReservation?: number | null;
  maxPerReservation?: number | null;
  // L-2: 노출 예약(예약발행). enabled=true 면 [displayStartAt, displayEndAt] 윈도우로 공개 노출 게이트.
  //   BE PopupStoreAdminView additive. start/end 는 Instant ISO-8601(또는 null). end<start → BE 400.
  displayReservationEnabled?: boolean | null;
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  // QA-189(V109): 예매 오픈 일시(노출≠예매오픈). null=분리없음. Instant ISO-8601. admin additive.
  bookingOpenAt?: string | null;
  // 직원확인 비밀번호: 토글 상태 + 설정 여부만 노출(해시/평문 비노출, BE additive).
  //   staffEntryPasswordEnabled=토글 ON/OFF, staffEntryPasswordSet=비번 해시 존재 여부(프리필 표시용).
  staffEntryPasswordEnabled?: boolean | null;
  staffEntryPasswordSet?: boolean | null;
  // QA-122 — admin GET 응답의 4로케일 번역 맵(미입력 locale 은 빈 dto). 로케일 탭 prefill 용.
  translations?: Record<string, PopupTranslation> | null;
}

export interface PopupRequest {
  popupId?: string; // A-2: 미전송 시 서버 자동발급
  popupKey?: string;
  // v2 base 필수 — name + 일정 + 표시 메타.
  name: string;
  openDate: string;
  closeDate: string;
  thumbnailUrl: string;
  accentHex: string;
  isActive: boolean;
  sortOrder: number;
  // BUG-022 통합(옵션 A, derive v2): 구조화(config) 필드만 전송.
  // location/address/hoursLabel/description/operatingUntil 는 BE 가 config 에서 도출 → FE 미전송.
  operatingDays?: string | null; // CSV "MON,TUE"
  operatingHoursStart?: string | null; // "HH:mm:ss"
  operatingHoursEnd?: string | null; // "HH:mm:ss"
  locationRegion?: string | null;
  locationAddress?: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
  descriptionHtml?: string | null;
  snsUrls?: string | null; // JSON array string
  basePrice?: number | null;
  // ID36: 일별 총 정원(null=무제한), 잔여석 공개 노출 토글. PopupStoreRequest 에 포함.
  dailyCapacity?: number | null;
  showRemainingSeats?: boolean | null;
  // QA-183(V109): 예약 수량 제한. 전부 null=무제한(임의 디폴트 강제 금지).
  //  - maxPerUser: 동일 ID(회원)당 누적 예매 수량 상한.
  //  - min/maxPerReservation: 1회 예매당 최소/최대 수량.
  maxPerUser?: number | null;
  minPerReservation?: number | null;
  maxPerReservation?: number | null;
  // 아래는 BE 도출 대상 — 일반적으로 미전송(optional 유지, 과거 호환용).
  location?: string;
  address?: string;
  hoursLabel?: string;
  description?: string;
  operatingUntil?: string | null;
  // L-2: 등록 시 예약 슬롯 동시 생성(선택). create 에서만 전송, update 에서는 미전송(슬롯은 /slots 로 관리).
  slots?: PopupSlotCreateInput[];
  // QA-122 — locale 별 description/descriptionHtml. null/생략 = 미변경(기존 번역 보존).
  translations?: Record<string, PopupTranslation>;
}

// L-2: 팝업 등록 폼에서 입력하는 슬롯 1행. wire 형식은 BE PopupSlotRequest 와 동일.
export interface PopupSlotCreateInput {
  slotDate: string; // YYYY-MM-DD
  timeStart: string; // "HH:mm"
  timeEnd: string; // "HH:mm"
  maxParticipants: number;
}

export interface PopupReservationView {
  reservationId: number;
  popupId: string;
  userId: string;
  slotDate: string;
  slotTime: string;
  peopleCount: number;
  status: string;
  createdAt: string;
}

export type EventStatus = 'UPCOMING' | 'ONGOING' | 'ENDED';

// 추첨 방식 안내 메타데이터 — 시스템 selection algorithm 분기에 사용되지 않으며,
// 모든 추첨은 admin 수동 (bulk-mark) 으로 처리된다.
export type DrawingMethod = 'RANDOM' | 'FCFS' | 'WEIGHTED';

// 발표 알림 채널 — EMAIL/SMS/KAKAO.
export type ContactChannel = 'EMAIL' | 'SMS' | 'KAKAO';

// QA-122 — 이벤트 캠페인 per-locale 번역(be-event EventTranslationDto, V77). locale 별 title/description.
// 정본(ko-KR)은 title/description 본필드. description = 본문 HTML. 빈 dto = SF ko 폴백.
export interface EventTranslation {
  title?: string | null;
  description?: string | null;
}

export interface EventAdminView {
  eventId: string;
  eventKey: string;
  title: string;
  description: string | null;
  thumbnailUrl: string;
  startDate: string;
  endDate: string;
  status: EventStatus;
  isWinnersPublished: boolean;
  // QA-127: 당첨자 발표 유무. true=발표 이벤트(당첨자 발표 예정/대상), false=미발표(당첨자 없음).
  //   BE additive(EventCampaignAdminView) — 미존재(undefined) 시 발표 이벤트로 간주(round-trip 안전).
  winnerAnnounceEnabled?: boolean;
  isActive: boolean;
  sortOrder: number;
  winnerCount: number | null;
  drawingMethod: DrawingMethod | null;
  contactChannel: ContactChannel | null;
  consolationMessage: string | null;
  // ID46: 노출 예약 게이트. true 면 BE 가 [startDate,endDate](KST inclusive) 윈도우로 공개 게이트.
  // 기존 isActive 와 병존(둘 다 통과해야 노출). BE additive — 미존재 시 undefined.
  displayReservationEnabled?: boolean;
  // 15-D-3(ID86): 이벤트 시작/종료 "시각"(Instant ISO). startDate/endDate(날짜)와 별개 additive 필드.
  // BE additive(EventCampaignAdminView 트레일링) — codegen 미반영 시 hand-maintained 확장. null=시각 미지정.
  startAt?: string | null;
  endAt?: string | null;
  createdAt: string;
  updatedAt: string;
  // QA-122 — admin GET 응답의 4로케일 번역 맵(미입력 locale 은 빈 dto). 로케일 탭 prefill 용.
  translations?: Record<string, EventTranslation> | null;
}

export interface EventRequest {
  eventId: string;
  eventKey: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  startDate: string;
  endDate: string;
  status: EventStatus;
  isActive: boolean;
  sortOrder: number;
  winnerCount: number | null;
  drawingMethod: DrawingMethod | null;
  contactChannel: ContactChannel | null;
  consolationMessage: string;
  // QA-127: 당첨자 발표 유무(true=발표 이벤트, false=미발표 이벤트). displayReservationEnabled 와 동일하게
  //   primitive boolean 으로 round-trip. BE EventCampaignRequest 가 소비 — 생략 시 BE 가 발표(true) 폴백.
  winnerAnnounceEnabled: boolean;
  // ID46: 노출 예약 게이트(미지정 시 BE 기본 false). round-trip.
  displayReservationEnabled: boolean;
  // 15-D-3(ID86): 이벤트 시작/종료 "시각"(Instant ISO, optional). 미지정(null/생략)=시각 미지정=기존 동작(날짜만).
  // BE EventCampaignRequest 트레일링 additive. endAt < startAt 은 BE 가 거부(둘 다 지정 시).
  startAt?: string | null;
  endAt?: string | null;
  // QA-122 — locale 별 title/description. null/생략 = 미변경(기존 번역 보존).
  translations?: Record<string, EventTranslation>;
}

// QA-122 — 당첨자 발표 per-locale 번역(be-event EventWinnerTranslationDto, V100). locale 별 title/body.
// 정본(ko-KR)은 title/body 본필드. body = 발표 본문 HTML. 빈 dto = SF ko 폴백.
export interface EventWinnerTranslation {
  title?: string | null;
  body?: string | null;
}

// C-8: 당첨자 발표 게시글 (BE EventWinner). create=POST /admin/v1/events/{eventId}/winners.
/** QA-481 당첨자 발표 노출 상태 — ALWAYS 노출 / SCHEDULED 예약 노출(시작~종료) / HIDDEN 노출 안 함. */
export type WinnerDisplayStatus = 'ALWAYS' | 'SCHEDULED' | 'HIDDEN';

export interface EventWinnerRequest {
  // QA-406 — 게시글 번호는 서버가 매긴다(당첨자 글 전체 최대 + 1, 수정 시 불변 — catalog #58). BO 는 보내지 않는다.
  postNumber?: never;
  title: string;
  author: string;
  body: string;
  publishedAt: string; // YYYY-MM-DD (LocalDate) — 목록 표기용 발표일.
  // QA-124(15-D-7/D-9) — 노출 시작 시각(Instant ISO 'Z', optional, 초 포함).
  // NULL/미지정 = 즉시 노출(기존 동작). 지정 시 그 시각부터 SF 공개(isVisibleAt 게이트).
  displayStartAt?: string | null;
  // QA-122 — locale 별 title/body. null/생략 = 미변경(기존 번역 보존).
  translations?: Record<string, EventWinnerTranslation>;
  // QA-481 — 노출 상태(고도몰 게시판 isview: 1 노출 / 2 예약 노출 / 3 노출 안 함, catalog V123). 응답에 없으면 BE 반영 전.
  displayStatus?: WinnerDisplayStatus | null;
  /** 예약 노출 종료 시각(Instant). NULL = 종료 없음. SCHEDULED 일 때만 의미. */
  displayEndAt?: string | null;
}

export interface EventWinnerView {
  winnerId: number;
  eventId: string;
  postNumber: number;
  title: string;
  author: string;
  body: string;
  publishedAt: string;
  // QA-124 — 노출 시작 시각(Instant). NULL = 즉시 노출. BO 편집·SF 게이트에 사용.
  displayStartAt?: string | null;
  // QA-481 — 노출 상태(고도몰 게시판 isview: 1 노출 / 2 예약 노출 / 3 노출 안 함, catalog V123). 응답에 없으면 BE 반영 전.
  displayStatus?: WinnerDisplayStatus | null;
  /** 예약 노출 종료 시각(Instant). NULL = 종료 없음. SCHEDULED 일 때만 의미. */
  displayEndAt?: string | null;
  // QA-122 — admin GET 응답의 4로케일 번역 맵(미입력 locale 은 빈 dto). 로케일 탭 prefill 용.
  translations?: Record<string, EventWinnerTranslation> | null;
}

export interface EventAnnounceRequest {
  sendNotification: boolean;
  channel: ContactChannel | null;
}

export interface EventAnnounceResponse {
  announced: boolean;
  sentWinners: number;
  sentLosers: number;
  skipped: number;
}

export interface EventApplicationBulkMarkRequest {
  winnerIds: string[];
  loserIds: string[];
}

export interface EventApplicationBulkMarkResponse {
  markedWinners: number;
  markedLosers: number;
  skipped: number;
}
